import { PROFILE_POLL_ATTEMPTS, PROFILE_POLL_INTERVAL_MS } from "@/src/auth/constants/auth.constants";
import { authService } from "@/src/auth/services/auth.service";
import type { AuthSession, ProfileRow } from "@/src/auth/types/auth.types";
import { createAppThunk } from "@/src/common/redux/create-app-thunk";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function loadProfileWithRetry(userId: string): Promise<{
	profile: ProfileRow | null;
	schoolIds: string[];
}> {
	for (let attempt = 0; attempt < PROFILE_POLL_ATTEMPTS; attempt += 1) {
		const result = await authService.fetchProfileWithSchools(userId);
		if (result) return result;
		if (attempt < PROFILE_POLL_ATTEMPTS - 1) await sleep(PROFILE_POLL_INTERVAL_MS);
	}
	return { profile: null, schoolIds: [] };
}

export const initializeAuth = createAppThunk<{
	session: AuthSession | null;
	profile: ProfileRow | null;
	schoolIds: string[];
}>("auth/initialize", async () => {
	const session = await authService.getSession();
	if (!session) return { session: null, profile: null, schoolIds: [] };
	const { profile, schoolIds } = await loadProfileWithRetry(session.user.id);
	return { session, profile, schoolIds };
});

export const refreshProfile = createAppThunk<{ profile: ProfileRow | null; schoolIds: string[] }, void>(
	"auth/refreshProfile",
	async (_, { getState }) => {
		const state = getState() as { auth?: { session: AuthSession | null } };
		const session = state.auth?.session ?? null;
		if (!session) return { profile: null, schoolIds: [] };
		const result = await authService.fetchProfileWithSchools(session.user.id);
		return result ?? { profile: null, schoolIds: [] };
	},
);

export const logout = createAppThunk<void>("auth/logout", async () => {
	await authService.signOut();
});
